import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Field } from "../Shared";
import { PROG } from "../../constants/testIds";

export function ProgramForm({ initial, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial.program_id;
  const [form, setForm] = useState({
    name: initial.name || "",
    description: initial.description || "",
    duration_days: initial.duration_days || 14,
    status: initial.status || "DRAFT",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, duration_days: Number(form.duration_days) };
      if (isEdit) await api.patch(`/admin/programs/${initial.program_id}`, payload);
      else await api.post("/admin/programs", payload);
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) {
      toast.error(extractError(err).message);
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid={PROG.programForm} className="bg-[#10121B] border-[#FF3B30]/30">
      <CardContent className="p-6">
        <h3 className="font-[Outfit] font-bold text-white text-lg mb-4">{isEdit ? t("programs.editProgram") : t("programs.newProgram")}</h3>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("programs.name")} testId={PROG.programName} value={form.name} onChange={set("name")} required />
          <Field label={t("programs.durationDays")} testId={PROG.programDuration} type="number" min={1} max={365} step={1} value={form.duration_days} onChange={set("duration_days")} required />
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-slate-300 text-sm">{t("programs.description")}</Label>
            <textarea data-testid={PROG.programDescription} value={form.description} onChange={set("description")} rows={3} className="w-full rounded-md bg-[#1C202E] border border-white/10 text-white text-sm p-3 outline-none focus:border-[#FF3B30]/60" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-sm">{t("common.status")}</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid={PROG.programStatus} className="bg-[#1C202E] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                {["DRAFT", "ACTIVE", "ARCHIVED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-white/5">{t("common.cancel")}</Button>
            <Button data-testid={PROG.programSave} type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{busy ? t("common.loading") : t("common.save")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
